import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { AlertCircle, Check, Link2, Server, Shield, Trash2 } from 'lucide-react';
import WifiTetheringIcon from '@mui/icons-material/WifiTethering';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useProjectName } from '@/app/layout/ProjectNameContext';
import sentinelNetwork from '@/lib/api/sentinelNetwork';
import { encryptString, fetchEntitlements } from '@/lib/crypto';
import { triggerScanPipeline } from '@/lib/api/pipelineService';
import { triggerAiScan } from '@/lib/api/aiKpiService';
import {
  InlineProjectList,
  TrivyIntegration,
  PolarisIntegration,
  EditMappingsDialog,
  SonarReportDialog,
  BlackDuckReportDialog,
  DeleteIntegrationConfirmDialog,
  extractBaseUrl,
} from './components';
import type { DataSourceItem } from './components';

type ValidationState = { loading: boolean; result: { ok: boolean; message: string } | null };

const ValidationChip = ({ state }: { state: ValidationState }) => {
  if (!state || (!state.loading && !state.result)) return null;
  if (state.loading) return <Chip icon={<CircularProgress size={12} />} label="Validating…" size="small" variant="outlined" />;
  if (state.result?.ok) return <Chip icon={<Check size={14} />} label={state.result.message || 'Connected'} size="small" color="success" variant="outlined" />;
  return <Chip icon={<AlertCircle size={14} />} label={state.result?.message || 'Failed'} size="small" color="error" variant="outlined" />;
};

export default function ConnectsPage() {
  const { appId } = useParams<{ appId: string }>();
  const theme = useTheme();
  const { refreshProject } = useProjectName();

  const [loading, setLoading] = useState(true);
  const [dataSources, setDataSources] = useState<DataSourceItem[]>([]);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // SonarQube inline form state
  const [sonarQubeUrl, setSonarQubeUrl] = useState('https://sonarcloud.io');
  const [sonarQubeToken, setSonarQubeToken] = useState('');
  const [sonarQubeScope] = useState<'shared'>('shared');
  const [sonarQubeOrganization, setSonarQubeOrganization] = useState('');
  const [sonarValidation, setSonarValidation] = useState<{ loading: boolean; result: { ok: boolean; message: string } | null }>({ loading: false, result: null });
  const [showSonarUpdateForm, setShowSonarUpdateForm] = useState(false);

  // BlackDuck inline form state
  const [blackDuckUrl, setBlackDuckUrl] = useState('https://mercedes-benz.app.blackduck.com');
  const [blackDuckToken, setBlackDuckToken] = useState('');
  const [blackDuckScope] = useState<'shared'>('shared');
  const [blackDuckValidation, setBlackDuckValidation] = useState<{ loading: boolean; result: { ok: boolean; message: string } | null }>({ loading: false, result: null });
  const [showBdUpdateForm, setShowBdUpdateForm] = useState(false);

  // Derived: true when the configured URL is sonarcloud.io (org is required); false for self-hosted SonarQube
  const isSonarCloud = sonarQubeUrl.trim().toLowerCase().includes('sonarcloud.io');

  // Project mappings for SonarQube, BlackDuck, and Trivy
  const [sonarProjectMappings, setSonarProjectMappings] = useState<Array<{ repoName: string; projectKey: string }>>([]);
  const [blackDuckProjectMappings, setBlackDuckProjectMappings] = useState<Array<{ repoName: string; projectKey: string }>>([]);
  const [trivyMappings, setTrivyMappings] = useState<Array<{ repoName: string; projectKey: string }>>([]);

  // Trivy image selection
  const [trivyImages, setTrivyImages] = useState<string[]>([]);

  // Edit mappings dialog state
  const [editMappingsDialog, setEditMappingsDialog] = useState<{ open: boolean; platform: 'SonarQube' | 'BlackDuck' | 'Harbor' | null; mappings: Array<{ repoName: string; projectKey: string }> }>({ open: false, platform: null, mappings: [] });

  // SonarCloud Report Dialog state
  const [sonarReportDialog, setSonarReportDialog] = useState<{ open: boolean; loading: boolean; data: any; error: string }>({ open: false, loading: false, data: null, error: '' });

  // BlackDuck Report Dialog state
  const [blackDuckReportDialog, setBlackDuckReportDialog] = useState<{ open: boolean; loading: boolean; data: any; error: string }>({ open: false, loading: false, data: null, error: '' });

  // Delete integration confirmation dialog state
  const [deleteIntegrationDialog, setDeleteIntegrationDialog] = useState<{ open: boolean; platform: string; url: string }>({ open: false, platform: '', url: '' });

  // Project tokens (unified: SonarCloud, BlackDuck)
  const [projectTokens, setProjectTokens] = useState<{ platform: string; url: string; encryptedConfig?: string; organization?: string; mappings?: Array<{ repoName: string; projectKey: string }>; scope?: string }[]>([]);

  // Current user info (for access control)
  const [currentUserRole, setCurrentUserRole] = useState('');
  const isAdmin = currentUserRole.includes('SENTINEL_POWER_USER') || currentUserRole.includes('SENTINEL_PROJECT_CREATION');

  useEffect(() => {
    fetchData();
  }, [appId]);

  const fetchData = async () => {
    if (!appId) return;
    setLoading(true);
    try {
      // Load data sources (to get Git repos for mappings + existing SonarQube/BlackDuck entries)
      const response = await sentinelNetwork.get(`/config/project/${appId}/data-sources`);
      const loadedDs: DataSourceItem[] = response.data.data || [];
      setDataSources(loadedDs);

      // Load user entitlements
      try {
        const entRes = await sentinelNetwork.get('/entitlements');
        const entData = entRes.data;
        setCurrentUserRole((entData?.roles || []).join(','));
      } catch { /* non-critical */ }

      // Load project tokens
      try {
        const tokenRes = await sentinelNetwork.get(`/config/project/${appId}/tokens`);
        const tokens = tokenRes.data?.data || [];
        setProjectTokens(tokens);

        // Restore SonarQube/BlackDuck URLs, orgs, and mappings from tokens
        const sonarToken = tokens.find((t: any) => t.platform === 'SonarQube');
        if (sonarToken) {
          if (sonarToken.url) setSonarQubeUrl(sonarToken.url);
          if (sonarToken.organization) {
            setSonarQubeOrganization(sonarToken.organization);
            setDataSources(prev => prev.map(ds => ds.platform === 'SonarQube' ? { ...ds, organization: sonarToken.organization, scope: sonarToken.scope } : ds));
          }
          if (sonarToken.mappings) setSonarProjectMappings(sonarToken.mappings);
        }
        const bdToken = tokens.find((t: any) => t.platform === 'BlackDuck');
        if (bdToken) {
          if (bdToken.url) setBlackDuckUrl(bdToken.url);
          if (bdToken.scope) {
            setDataSources(prev => prev.map(ds => ds.platform === 'BlackDuck' ? { ...ds, scope: bdToken.scope } : ds));
          }
          if (bdToken.mappings) setBlackDuckProjectMappings(bdToken.mappings);
        }
        const harborToken = tokens.find((t: any) => t.platform === 'Harbor' || t.platform === 'Trivy');
        if (harborToken?.images) setTrivyImages(harborToken.images);
        if (harborToken?.mappings) setTrivyMappings(harborToken.mappings);
      } catch {
        setProjectTokens([]);
      }
    } catch (err: any) {
      console.error('Failed to fetch connects data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProjectToken = async (platform: string, url: string) => {
    try {
      await sentinelNetwork.delete(`/config/project/${appId}/tokens`, {
        data: { platform },
      });
      try {
        await sentinelNetwork.delete(`/config/project/${appId}/data-sources`, {
          data: { url },
        });
      } catch { /* non-critical if data source already absent */ }
      setSnackbar({ open: true, message: `${platform} integration removed`, severity: 'success' });
      await fetchData();
    } catch (err: any) {
      setSnackbar({ open: true, message: `Failed to remove ${platform} integration`, severity: 'error' });
    }
  };

  /** Fire-and-forget: kick off the Delta AI KPI Batch scan for all configured git repos. */
  const triggerAiKpiScan = () => {
    if (!appId) return;
    const gitRepos = dataSources
      .filter((ds) => ds.platform === 'Git' && ds.url?.trim())
      .map((ds) => {
        const match = ds.url.match(/\/([^/]+?)(?:\.git)?$/);
        return { repoSlug: match ? match[1] : ds.url, url: ds.url, branch: (ds as any).branch || 'main' };
      });
    if (gitRepos.length === 0) return;
    triggerAiScan(appId, gitRepos).catch((e) =>
      console.warn('[ConnectsPage] Delta AI KPI scan trigger failed:', e?.message),
    );
  };

  const handleSonarQubeVerify = async () => {
    if (!sonarQubeUrl.trim() || !sonarQubeToken.trim() || (isSonarCloud && !sonarQubeOrganization.trim())) {
      setSonarValidation({ loading: false, result: { ok: false, message: isSonarCloud ? 'URL, Organization, and Token are required' : 'URL and Token are required' } });
      return;
    }

    setSonarValidation({ loading: true, result: null });
    try {
      const baseUrl = extractBaseUrl(sonarQubeUrl);

      const res = await sentinelNetwork.post('/config/onboarding-secops/validate/sonar', {
        url: baseUrl,
        token: sonarQubeToken,
      });
      const result = res.data;
      if (!result.ok) {
        setSonarValidation({ loading: false, result: { ok: false, message: result.message || 'Validation failed. Check your token and try again.' } });
        return;
      }

      const { publicKey } = await fetchEntitlements();
      const configObj = { baseUrl, token: sonarQubeToken, organization: sonarQubeOrganization, platform: 'SonarQube', scope: sonarQubeScope, projectMappings: sonarProjectMappings };
      const encryptedConfig = encryptString(JSON.stringify(configObj), publicKey);

      await sentinelNetwork.post(`/config/project/${appId}/tokens`, {
        platform: 'SonarQube', encryptedConfig, url: baseUrl,
        organization: sonarQubeOrganization, mappings: sonarProjectMappings, scope: sonarQubeScope,
      });
      await sentinelNetwork.post(`/config/project/${appId}/data-sources`, {
        platform: 'SonarQube', url: baseUrl,
      });
      setSonarValidation({ loading: false, result: { ok: true, message: 'SonarQube connected' } });
      setSnackbar({ open: true, message: 'SonarQube connected — scan queued', severity: 'success' });

      await fetchData();
      if (appId) await refreshProject(appId);
      // Trigger tool scan pipeline so the new token is picked up immediately
      if (appId) triggerScanPipeline(appId, 'token-update').catch((e) => console.warn('[ConnectsPage] scan trigger failed:', e?.message));
      triggerAiKpiScan();
      setSonarQubeToken(''); // clear token only — URL stays pre-filled for reference
      setShowSonarUpdateForm(false);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Unknown error';
      setSonarValidation({ loading: false, result: { ok: false, message: `Failed: ${msg}` } });
    }
  };

  const handleBlackDuckVerify = async () => {
    if (!blackDuckUrl.trim() || !blackDuckToken.trim()) {
      setBlackDuckValidation({ loading: false, result: { ok: false, message: 'URL and Token are required' } });
      return;
    }

    setBlackDuckValidation({ loading: true, result: null });
    try {
      const baseUrl = extractBaseUrl(blackDuckUrl);

      const res = await sentinelNetwork.post('/config/onboarding-secops/validate/blackduck', {
        url: baseUrl,
        token: blackDuckToken,
      });
      const result = res.data;
      if (!result.ok) {
        setBlackDuckValidation({ loading: false, result: { ok: false, message: result.message || 'Validation failed. Check your token and try again.' } });
        return;
      }

      const { publicKey } = await fetchEntitlements();
      const configObj = { baseUrl, token: blackDuckToken, platform: 'BlackDuck', scope: blackDuckScope, projectMappings: blackDuckProjectMappings };
      const encryptedConfig = encryptString(JSON.stringify(configObj), publicKey);

      await sentinelNetwork.post(`/config/project/${appId}/tokens`, {
        platform: 'BlackDuck', encryptedConfig, url: baseUrl,
        mappings: blackDuckProjectMappings, scope: blackDuckScope,
      });
      await sentinelNetwork.post(`/config/project/${appId}/data-sources`, {
        platform: 'BlackDuck', url: baseUrl,
      });
      setBlackDuckValidation({ loading: false, result: { ok: true, message: 'BlackDuck connected' } });
      setSnackbar({ open: true, message: 'BlackDuck connected — scan queued', severity: 'success' });

      await fetchData();
      if (appId) await refreshProject(appId);
      // Trigger tool scan pipeline so the new token is picked up immediately
      if (appId) triggerScanPipeline(appId, 'token-update').catch((e) => console.warn('[ConnectsPage] scan trigger failed:', e?.message));
      triggerAiKpiScan();
      setBlackDuckToken(''); // clear token only — URL stays pre-filled for reference
      setShowBdUpdateForm(false);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Unknown error';
      setBlackDuckValidation({ loading: false, result: { ok: false, message: `Failed: ${msg}` } });
    }
  };

  const handleFetchSonarReport = async (configUrl: string, configOrg?: string, projectMappings?: Array<{ repoName: string; projectKey: string }>) => {
    setSonarReportDialog({ open: true, loading: true, data: null, error: '' });
    try {
      const gitRepos = dataSources.filter(ds => ds.platform === 'Git').map(ds => {
        const urlParts = ds.url.replace('.git', '').split('/');
        const repoName = urlParts[urlParts.length - 1];
        return { name: repoName, branch: ds.branch || 'main' };
      });

      if (gitRepos.length === 0) {
        setSonarReportDialog({ open: true, loading: false, data: null, error: 'No Git repositories configured. Add Git repos first.' });
        return;
      }

      const sonarToken = projectTokens.find(t => t.platform === 'SonarQube') as any;

      if (!sonarToken?.encryptedConfig) {
        setSonarReportDialog({ open: true, loading: false, data: null, error: 'SonarQube configuration not found. Please reconfigure with token.' });
        return;
      }

      const res = await sentinelNetwork.post('/fetch-sonar-overview', {
        encryptedToken: sonarToken.encryptedConfig,
        organization: configOrg || '',
        baseUrl: configUrl,
        repos: gitRepos,
        projectMappings: projectMappings || []
      });

      if (res.data.success) {
        setSonarReportDialog({ open: true, loading: false, data: res.data, error: '' });
      } else {
        setSonarReportDialog({ open: true, loading: false, data: null, error: res.data.message || 'Failed to fetch report' });
      }
    } catch (err: any) {
      setSonarReportDialog({ open: true, loading: false, data: null, error: err?.response?.data?.message || err?.message || 'Failed to fetch report' });
    }
  };

  const handleFetchBlackDuckReport = async (configUrl: string, projectMappings?: Array<{ repoName: string; projectKey: string }>) => {
    setBlackDuckReportDialog({ open: true, loading: true, data: null, error: '' });
    try {
      const gitRepos = dataSources.filter(ds => ds.platform === 'Git').map(ds => {
        const urlParts = ds.url.replace('.git', '').split('/');
        const repoName = urlParts[urlParts.length - 1];
        return { name: repoName, branch: (ds as any).branch || 'main' };
      });

      // When BD project mappings are configured (Discover & Select mode), the backend
      // iterates over the selected BD project keys directly — git repos are not required.
      // Only block when there are neither git repos nor BD project mappings.
      const hasMappings = projectMappings && projectMappings.length > 0;
      if (gitRepos.length === 0 && !hasMappings) {
        setBlackDuckReportDialog({ open: true, loading: false, data: null, error: 'No Git repositories configured. Add Git repos first.' });
        return;
      }

      const bdToken = projectTokens.find(t => t.platform === 'BlackDuck') as any;

      if (!bdToken?.encryptedConfig) {
        setBlackDuckReportDialog({ open: true, loading: false, data: null, error: 'BlackDuck configuration not found. Please reconfigure with token.' });
        return;
      }

      const res = await sentinelNetwork.post('/fetch-blackduck-overview', {
        encryptedToken: bdToken.encryptedConfig,
        baseUrl: configUrl,
        repos: gitRepos,
        projectMappings: projectMappings || []
      });

      if (res.data.success) {
        setBlackDuckReportDialog({ open: true, loading: false, data: res.data, error: '' });
      } else {
        setBlackDuckReportDialog({ open: true, loading: false, data: null, error: res.data.message || 'Failed to fetch report' });
      }
    } catch (err: any) {
      setBlackDuckReportDialog({ open: true, loading: false, data: null, error: err?.response?.data?.message || err?.message || 'Failed to fetch report' });
    }
  };

  const handleSaveSonarMappings = async (mappings: Array<{ repoName: string; projectKey: string }>) => {
    setSonarProjectMappings(mappings);
    const token = projectTokens.find((t) => t.platform === 'SonarQube') as any;
    if (token?.encryptedConfig) {
      await sentinelNetwork.post(`/config/project/${appId}/tokens`, {
        platform: 'SonarQube',
        encryptedConfig: token.encryptedConfig,
        url: token.url,
        organization: token.organization,
        mappings,
        scope: token.scope,
      });
    }
    setSnackbar({ open: true, message: 'SonarQube project selection saved', severity: 'success' });
    if (appId) triggerScanPipeline(appId, 'token-update').catch((e) => console.warn('[ConnectsPage] scan trigger failed:', e?.message));
    triggerAiKpiScan();
  };

  const handleSaveBlackDuckMappings = async (mappings: Array<{ repoName: string; projectKey: string }>) => {
    setBlackDuckProjectMappings(mappings);
    const token = projectTokens.find((t) => t.platform === 'BlackDuck') as any;
    if (token?.encryptedConfig) {
      await sentinelNetwork.post(`/config/project/${appId}/tokens`, {
        platform: 'BlackDuck',
        encryptedConfig: token.encryptedConfig,
        url: token.url,
        mappings,
        scope: token.scope,
      });
    }
    setSnackbar({ open: true, message: 'BlackDuck project selection saved', severity: 'success' });
    if (appId) triggerScanPipeline(appId, 'token-update').catch((e) => console.warn('[ConnectsPage] scan trigger failed:', e?.message));
    triggerAiKpiScan();
  };

  const handleSaveTrivyImages = async (images: string[]) => {
    setTrivyImages(images);
    const token = projectTokens.find((t: any) => t.platform === 'Harbor' || t.platform === 'Trivy') as any;
    if (token?.encryptedConfig) {
      await sentinelNetwork.post(`/config/project/${appId}/tokens`, {
        platform: token.platform || 'Harbor',
        encryptedConfig: token.encryptedConfig,
        url: token.url,
        images,
        mappings: trivyMappings,
      });
    }
    setSnackbar({ open: true, message: 'Container images saved', severity: 'success' });
    if (appId) triggerScanPipeline(appId, 'token-update').catch((e) => console.warn('[ConnectsPage] scan trigger failed:', e?.message));
    triggerAiKpiScan();
  };

  const handleSaveTrivyMappings = async (mappings: Array<{ repoName: string; projectKey: string }>) => {
    setTrivyMappings(mappings);
    const token = projectTokens.find((t: any) => t.platform === 'Harbor' || t.platform === 'Trivy') as any;
    if (token?.encryptedConfig) {
      try {
        await sentinelNetwork.post(`/config/project/${appId}/tokens`, {
          platform: token.platform || 'Harbor',
          encryptedConfig: token.encryptedConfig,
          url: token.url,
          images: trivyImages,
          mappings,
        });
      } catch { /* non-critical */ }
    }
    setSnackbar({ open: true, message: 'Trivy image mappings saved', severity: 'success' });
    if (appId) triggerScanPipeline(appId, 'token-update').catch((e) => console.warn('[ConnectsPage] scan trigger failed:', e?.message));
    triggerAiKpiScan();
  };

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100%" width="100%">
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Box sx={{ p: 1.5 }}>
      <Box>
        {/* Header */}
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
              }}
            >
              <Link2 size={24} color={theme.palette.primary.main} />
            </Box>
            <Typography variant="h5" fontWeight={700}>
              Connects
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Manage tool integrations — SonarQube, BlackDuck, Trivy, and Polaris
          </Typography>
        </Box>

        {/* ── SonarQube Integration ─────────────────────────────────── */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <DeviceHubIcon fontSize="small" color="primary" />
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'text.primary' }}>SonarQube Integration</Typography>
          </Box>

            {(() => {
              const sonarDs = dataSources.find(ds => ds.platform === 'SonarQube');
              const sonarToken = projectTokens.find(t => t.platform === 'SonarQube');
              const existing = sonarDs || sonarToken;
              return (
                <Box sx={{ mb: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600}>SonarQube</Typography>
                    {existing ? (
                      <Chip icon={<Check size={12} />} label="Connected" size="small" color="success" sx={{ fontSize: 11, height: 22 }} />
                    ) : (
                      <Chip label="Not configured" size="small" variant="outlined" sx={{ fontSize: 11, height: 22 }} />
                    )}
                  </Stack>
                  {existing ? (
                    <Box sx={{ mb: 1 }}>
                      {/* URL (editable) + actions row */}
                      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1} sx={{ mb: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, flexWrap: 'wrap' }}>
                          <TextField
                            label="SonarQube URL"
                            size="small"
                            value={sonarQubeUrl}
                            onChange={(e) => { setSonarQubeUrl(e.target.value); setSonarValidation({ loading: false, result: null }); }}
                            sx={{ minWidth: 240, maxWidth: 360 }}
                          />
                          {(sonarToken?.organization || sonarDs?.organization) && (
                            <Chip label={sonarToken?.organization || sonarDs?.organization} size="small" variant="outlined" sx={{ fontSize: 11, height: 22 }} />
                          )}
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                          {isAdmin && (
                            <Button size="small" variant="text"
                              onClick={() => setShowSonarUpdateForm((v) => !v)}
                              sx={{ textTransform: 'none', fontSize: 11 }}>
                              {showSonarUpdateForm ? 'Cancel Update' : 'Update Token'}
                            </Button>
                          )}
                          <Tooltip title={sonarProjectMappings.length === 0 ? 'Select at least one project below before fetching' : ''} arrow>
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={sonarProjectMappings.length === 0}
                                onClick={() => handleFetchSonarReport(sonarQubeUrl, sonarToken?.organization || sonarDs?.organization, sonarProjectMappings)}
                                startIcon={<Server size={14} />}
                              >
                                Fetch Report
                              </Button>
                            </span>
                          </Tooltip>
                          {isAdmin && (
                            <IconButton size="small" color="error" onClick={() => setDeleteIntegrationDialog({ open: true, platform: 'SonarQube', url: existing.url })}>
                              <Trash2 size={16} />
                            </IconButton>
                          )}
                        </Stack>
                      </Stack>

                      {/* Inline update form (token re-entry) */}
                      <Collapse in={showSonarUpdateForm} unmountOnExit>
                        <Stack spacing={1.5} sx={{ mb: 1.5, p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.04), borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="caption" color="text.secondary">
                            Re-enter your SonarQube token to verify and apply the updated URL / credentials.
                          </Typography>
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                            <TextField label={isSonarCloud ? 'Organization *' : 'Organization (optional)'} placeholder="your-org" value={sonarQubeOrganization} onChange={(e) => setSonarQubeOrganization(e.target.value)} size="small" />
                            <TextField label="Token" type="password" placeholder="SonarQube Token" value={sonarQubeToken} onChange={(e) => { setSonarQubeToken(e.target.value); setSonarValidation({ loading: false, result: null }); }} size="small" />
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Button variant="outlined" size="small" onClick={handleSonarQubeVerify} disabled={sonarValidation.loading || !sonarQubeUrl.trim() || !sonarQubeToken.trim() || (isSonarCloud && !sonarQubeOrganization.trim())} startIcon={<WifiTetheringIcon sx={{ fontSize: 16 }} />} sx={{ textTransform: 'none' }}>
                              Verify &amp; Update
                            </Button>
                            <ValidationChip state={sonarValidation} />
                          </Box>
                        </Stack>
                      </Collapse>

                      {/* Warning when no projects selected */}
                      {sonarProjectMappings.length === 0 && (
                        <Alert severity="warning" sx={{ mb: 1, py: 0.5, fontSize: 12 }}>
                          Select at least one project below to enable <strong>Fetch Report</strong>.
                        </Alert>
                      )}

                      {/* Inline project picker — available to all project members */}
                      {!loading && appId && (
                        <InlineProjectList
                          appId={appId}
                          platform="SonarQube"
                          savedMappings={sonarProjectMappings}
                          onSave={handleSaveSonarMappings}
                          autoFetch
                          gitRepos={dataSources
                            .filter(ds => ds.platform === 'Git')
                            .map(ds => ({
                              name: ds.url.replace('.git', '').split('/').pop() || '',
                              branch: (ds as any).branch || 'main',
                            }))}
                        />
                      )}
                    </Box>
                  ) : (
                    <Stack spacing={1.5}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                        This token will be shared across all project members for SonarQube access.
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                        <TextField label="SonarQube URL" placeholder="https://sonarcloud.io or self-hosted URL" value={sonarQubeUrl} onChange={(e) => { setSonarQubeUrl(e.target.value); setSonarValidation({ loading: false, result: null }); }} size="small" helperText="e.g. https://sonarcloud.io or self-hosted URL" />
                        <TextField label={isSonarCloud ? 'Organization *' : 'Organization (optional)'} placeholder="your-org" value={sonarQubeOrganization} onChange={(e) => setSonarQubeOrganization(e.target.value)} size="small" helperText={isSonarCloud ? 'Organization key from SonarCloud' : 'Not required for self-hosted SonarQube'} />
                      </Box>
                      <TextField label="Token" type="password" placeholder="SonarQube Token" value={sonarQubeToken} onChange={(e) => { setSonarQubeToken(e.target.value); setSonarValidation({ loading: false, result: null }); }} size="small" fullWidth />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Button variant="outlined" size="small" onClick={handleSonarQubeVerify} disabled={sonarValidation.loading || !sonarQubeUrl.trim() || !sonarQubeToken.trim() || (isSonarCloud && !sonarQubeOrganization.trim())} startIcon={<WifiTetheringIcon sx={{ fontSize: 16 }} />} sx={{ textTransform: 'none' }}>
                          Verify &amp; Save
                        </Button>
                        <ValidationChip state={sonarValidation} />
                      </Box>
                    </Stack>
                  )}
                </Box>
              );
            })()}

            <Alert severity="info" sx={{ mt: 2, py: 0.5 }} icon={<AlertCircle size={16} />}>
              Tokens are encrypted before storage and shared across all project members for SonarQube access.
            </Alert>
        </Paper>

        {/* ── BlackDuck Integration ─────────────────────────────────── */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <DeviceHubIcon fontSize="small" color="primary" />
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'text.primary' }}>BlackDuck Integration</Typography>
          </Box>

            {(() => {
              const blackDuckDs = dataSources.find(ds => ds.platform === 'BlackDuck');
              const bdToken = projectTokens.find(t => t.platform === 'BlackDuck');
              const existing = blackDuckDs || bdToken;
              return (
                <Box sx={{ mb: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600}>BlackDuck</Typography>
                    {existing ? (
                      <Chip icon={<Check size={12} />} label="Connected" size="small" color="success" sx={{ fontSize: 11, height: 22 }} />
                    ) : (
                      <Chip label="Not configured" size="small" variant="outlined" sx={{ fontSize: 11, height: 22 }} />
                    )}
                  </Stack>
                  {existing ? (
                    <Box sx={{ mb: 1 }}>
                      {/* URL (editable) + actions row */}
                      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1} sx={{ mb: 1 }}>
                        <TextField
                          label="BlackDuck URL"
                          size="small"
                          value={blackDuckUrl}
                          onChange={(e) => { setBlackDuckUrl(e.target.value); setBlackDuckValidation({ loading: false, result: null }); }}
                          sx={{ minWidth: 240, maxWidth: 420, flex: 1 }}
                          helperText={blackDuckUrl.trim() ? `Base URL: ${extractBaseUrl(blackDuckUrl)}` : undefined}
                        />
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                          {isAdmin && (
                            <Button size="small" variant="text"
                              onClick={() => setShowBdUpdateForm((v) => !v)}
                              sx={{ textTransform: 'none', fontSize: 11 }}>
                              {showBdUpdateForm ? 'Cancel Update' : 'Update Token'}
                            </Button>
                          )}
                          <Tooltip title={blackDuckProjectMappings.length === 0 ? 'Select at least one project below before fetching' : ''} arrow>
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={blackDuckProjectMappings.length === 0}
                                onClick={() => handleFetchBlackDuckReport(blackDuckUrl, blackDuckProjectMappings)}
                                startIcon={<Shield size={14} />}
                              >
                                Fetch Report
                              </Button>
                            </span>
                          </Tooltip>
                          {isAdmin && (
                            <IconButton size="small" color="error" onClick={() => setDeleteIntegrationDialog({ open: true, platform: 'BlackDuck', url: existing.url })}>
                              <Trash2 size={16} />
                            </IconButton>
                          )}
                        </Stack>
                      </Stack>

                      {/* Inline update form (token re-entry) */}
                      <Collapse in={showBdUpdateForm} unmountOnExit>
                        <Stack spacing={1.5} sx={{ mb: 1.5, p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.04), borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="caption" color="text.secondary">
                            Re-enter your BlackDuck API token to verify and apply the updated URL / credentials.
                          </Typography>
                          <TextField label="API Token" type="password" placeholder="BlackDuck API Token" value={blackDuckToken} onChange={(e) => { setBlackDuckToken(e.target.value); setBlackDuckValidation({ loading: false, result: null }); }} size="small" fullWidth />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Button variant="outlined" size="small" onClick={handleBlackDuckVerify} disabled={blackDuckValidation.loading || !blackDuckUrl.trim() || !blackDuckToken.trim()} startIcon={<WifiTetheringIcon sx={{ fontSize: 16 }} />} sx={{ textTransform: 'none' }}>
                              Verify &amp; Update
                            </Button>
                            <ValidationChip state={blackDuckValidation} />
                          </Box>
                        </Stack>
                      </Collapse>

                      {/* Warning when no projects selected */}
                      {blackDuckProjectMappings.length === 0 && (
                        <Alert severity="warning" sx={{ mb: 1, py: 0.5, fontSize: 12 }}>
                          Select at least one project below to enable <strong>Fetch Report</strong>.
                        </Alert>
                      )}

                      {/* Inline project picker — available to all project members */}
                      {!loading && appId && (
                        <InlineProjectList
                          appId={appId}
                          platform="BlackDuck"
                          savedMappings={blackDuckProjectMappings}
                          onSave={handleSaveBlackDuckMappings}
                          autoFetch
                          gitRepos={dataSources
                            .filter(ds => ds.platform === 'Git')
                            .map(ds => ({
                              name: ds.url.replace('.git', '').split('/').pop() || '',
                              branch: (ds as any).branch || 'main',
                            }))}
                        />
                      )}
                    </Box>
                  ) : (
                    <Stack spacing={1.5}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                        This token will be shared across all project members for BlackDuck access.
                      </Typography>
                      <TextField label="BlackDuck URL" placeholder="https://mercedes-benz.app.blackduck.com" value={blackDuckUrl} onChange={(e) => { setBlackDuckUrl(e.target.value); setBlackDuckValidation({ loading: false, result: null }); }} size="small" fullWidth helperText={blackDuckUrl.trim() ? `Base URL: ${extractBaseUrl(blackDuckUrl)}` : 'e.g. https://mercedes-benz.app.blackduck.com'} />
                      <TextField label="API Token" type="password" placeholder="BlackDuck API Token" value={blackDuckToken} onChange={(e) => { setBlackDuckToken(e.target.value); setBlackDuckValidation({ loading: false, result: null }); }} size="small" fullWidth />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Button variant="outlined" size="small" onClick={handleBlackDuckVerify} disabled={blackDuckValidation.loading || !blackDuckUrl.trim() || !blackDuckToken.trim()} startIcon={<WifiTetheringIcon sx={{ fontSize: 16 }} />} sx={{ textTransform: 'none' }}>
                          Verify &amp; Save
                        </Button>
                        <ValidationChip state={blackDuckValidation} />
                      </Box>
                    </Stack>
                  )}
                </Box>
              );
            })()}

            <Alert severity="info" sx={{ mt: 2, py: 0.5 }} icon={<AlertCircle size={16} />}>
              Tokens are encrypted before storage and shared across all project members for BlackDuck access.
            </Alert>
        </Paper>

        {/* ── Trivy / Container Registry ─────────────────────────────── */}
        <TrivyIntegration
          appId={appId}
          projectTokens={projectTokens}
          savedImages={trivyImages}
          pageLoading={loading}
          onTokenChange={async () => {
            await fetchData();
            // Trigger tool scan pipeline so the new Trivy token is picked up immediately
            if (appId) triggerScanPipeline(appId, 'token-update').catch((e) => console.warn('[ConnectsPage] scan trigger failed:', e?.message));
            triggerAiKpiScan();
          }}
          onSaveImages={handleSaveTrivyImages}
          onSnackbar={(message, severity) => setSnackbar({ open: true, message, severity })}
        />

        {/* Edit Image Mappings — shown once Trivy token exists */}
        {projectTokens.some((t: any) => t.platform === 'Harbor' || t.platform === 'Trivy') && (
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
            <Stack>
              <Typography variant="body2" fontWeight={500}>Image Mappings</Typography>
              <Typography variant="caption" color="text.secondary">
                Map each Git repo to its Harbor image path when the names differ.
                {trivyMappings.length > 0 && ` ${trivyMappings.length} mapping${trivyMappings.length !== 1 ? 's' : ''} configured.`}
              </Typography>
            </Stack>
            <Button
              size="small"
              variant="outlined"
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
              onClick={() => setEditMappingsDialog({ open: true, platform: 'Harbor', mappings: trivyMappings })}
            >
              Edit Image Mappings
            </Button>
          </Stack>
        )}

        {/* ── Polaris ─────────────────────────────────────────────────── */}
        <PolarisIntegration
          appId={appId}
          projectTokens={projectTokens}
          onTokenChange={async () => {
            await fetchData();
            if (appId) triggerScanPipeline(appId, 'token-update').catch((e) => console.warn('[ConnectsPage] scan trigger failed:', e?.message));
          }}
          onSnackbar={(message, severity) => setSnackbar({ open: true, message, severity })}
        />
      </Box>

      {/* Edit Mappings Dialog */}
      <EditMappingsDialog
        open={editMappingsDialog.open}
        platform={editMappingsDialog.platform}
        mappings={editMappingsDialog.mappings}
        dataSources={dataSources}
        onClose={() => setEditMappingsDialog({ open: false, platform: null, mappings: [] })}
        onMappingsChange={(mappings) => setEditMappingsDialog(prev => ({ ...prev, mappings }))}
        onSave={async () => {
          const { mappings, platform } = editMappingsDialog;
          if (platform === 'SonarQube') setSonarProjectMappings(mappings);
          else if (platform === 'BlackDuck') setBlackDuckProjectMappings(mappings);
          else if (platform === 'Harbor') { await handleSaveTrivyMappings(mappings); }
          setEditMappingsDialog({ open: false, platform: null, mappings: [] });
          if (appId && platform && platform !== 'Harbor') {
            const token = projectTokens.find(t => t.platform === platform) as any;
            if (token?.encryptedConfig) {
              try {
                await sentinelNetwork.post(`/config/project/${appId}/tokens`, {
                  platform, encryptedConfig: token.encryptedConfig, url: token.url,
                  organization: token.organization, mappings, scope: token.scope,
                });
              } catch { /* non-critical — mappings saved in state */ }
            }
            if (appId) triggerScanPipeline(appId, 'token-update').catch((e) => console.warn('[ConnectsPage] scan trigger failed:', e?.message));
          }
        }}
      />

      {/* Delete Integration Confirmation Dialog */}
      <DeleteIntegrationConfirmDialog
        open={deleteIntegrationDialog.open}
        platform={deleteIntegrationDialog.platform}
        url={deleteIntegrationDialog.url}
        onClose={() => setDeleteIntegrationDialog({ open: false, platform: '', url: '' })}
        onConfirm={async () => {
          const platform = deleteIntegrationDialog.platform;
          await handleDeleteProjectToken(platform, deleteIntegrationDialog.url);
          setDeleteIntegrationDialog({ open: false, platform: '', url: '' });
        }}
      />

      {/* SonarQube Report Dialog */}
      <SonarReportDialog
        open={sonarReportDialog.open}
        loading={sonarReportDialog.loading}
        data={sonarReportDialog.data}
        error={sonarReportDialog.error}
        onClose={() => setSonarReportDialog({ open: false, loading: false, data: null, error: '' })}
      />

      {/* BlackDuck Report Dialog */}
      <BlackDuckReportDialog
        open={blackDuckReportDialog.open}
        loading={blackDuckReportDialog.loading}
        data={blackDuckReportDialog.data}
        error={blackDuckReportDialog.error}
        onClose={() => setBlackDuckReportDialog({ open: false, loading: false, data: null, error: '' })}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
